import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { message, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { AiOutlineOpenAI } from "react-icons/ai";
import { FaChevronLeft, FaChevronRight, FaMeta } from "react-icons/fa6";
import { SiAnthropic, SiGooglegemini } from "react-icons/si";

import "./styles.scss";
import ConversationChat from "./conversation-chat";
import ConversationHistory from "./conversation-history";
import InitializeChat from "./initialize-chat";
import ChatInput from "../../../assets/component/chat-input";
import { DocumentSearchAction, DocumentSearchReset } from "../../../store/slices/ai-chat/doc-research";

const SHOW_MODELS = false

export const ChatModels = [
    {
        id: 1,
        title: "Claude",
        modelName: "claude",
        icon: <SiAnthropic />,
    },
    {
        id: 2,
        title: "Chat GPT",
        modelName: "chat-gpt",
        icon: <AiOutlineOpenAI />,
    },
    {
        id: 3,
        title: "Gemini",
        modelName: "gemini",
        icon: <SiGooglegemini />
    },
    {
        id: 4,
        title: "Meta",
        modelName: "meta",
        icon: <FaMeta />
    },
]

const DocResearch = () => {
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [showHistorySection, setShowHistorySection] = useState(false);
    const [questionInput, setQuestionInput] = useState("");
    const [chatData, setChatData] = useState([])
    const [conversationId, setConversationId] = useState(null)
    const [lastConversation, setLastConversation] = useState([])
    const [selectedModel, setSelectedModel] = useState(null)
    const [showModelSelect, setShowModelSelect] = useState(true)

    const dispatch = useDispatch()
    const scrollRef = useRef()
    const { isLoading: chatLoading, error: chatError, data: chatResponse } = useSelector((state) => state.documentSearch)

    const scrollToBottom = () => {
        if (scrollRef.current) {
            setTimeout(() => {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100)
        }
    };

    const handleLocalStorage = (id) => {
        const data = {
            conversationId: id,
            files: uploadedFiles.map((file) => ({
                fileName: file.fileName,
                format: file.format
            }))
        }

        localStorage.setItem("doc-search-files", JSON.stringify(data))
    }

    const handleClearLocalStorage = () => {
        localStorage.setItem("doc-search-files", JSON.stringify({}))
    }

    const handleNewConversation = ({ model }) => {
        model?.modelName && setSelectedModel(model)
        dispatch(DocumentSearchReset())
        handleClearLocalStorage()
        setChatData([])
        setConversationId(null)
        setLastConversation([])
        setUploadedFiles([])
    }

    const handleExpandCollapse = () => {
        setShowHistorySection(!showHistorySection)
    }

    const structureChat = ({ question, answer, error, loading }) => {
        let validatedChat = chatData;

        if (loading) {
            const newChat = {
                type: "loading",
                text: ""
            }

            setChatData(prevChat => {
                return [...prevChat, newChat]
            })
            scrollToBottom()
        }

        if (question) {
            const newChat = {
                type: "question",
                text: question
            }

            setChatData(prevChat => {
                return [...prevChat, newChat]
            })

            scrollToBottom()
        }

        if (answer) {
            validatedChat = validatedChat?.filter(chat => chat.type !== "loading")
            const newChat = {
                type: "answer",
                text: answer
            }
            setChatData([...validatedChat, newChat])
        }

        if (error) {
            validatedChat = validatedChat?.filter(chat => chat.type !== "loading")
            const newChat = {
                type: "error",
                text: error
            }
            setChatData([...validatedChat, newChat])
            scrollToBottom()
        }
    }

    const checkValidations = () => {
        if (!questionInput) {
            message.error("Please add a question.")
        }

        if (uploadedFiles?.length === 0) {
            message.error("Please upload a document.")
        }

        if (SHOW_MODELS && !selectedModel) {
            message.error("Please select an AI model")
        }
    }

    const handleSubmit = () => {
        const storedFiles = JSON.parse(localStorage.getItem("doc-search-files"));

        if (!chatLoading) {
            if (!questionInput || (uploadedFiles?.length === 0) || (SHOW_MODELS && !selectedModel)) {
                checkValidations();
            } else {
                structureChat({ question: questionInput });
                const prevUploadedFiles = storedFiles?.files || [];

                const newFiles = uploadedFiles.filter(file => {
                    const isDuplicate = prevUploadedFiles.some(prevFile => {
                        return prevFile.fileName === file.fileName && prevFile.format === file.format;
                    });
                    return !isDuplicate;
                });
                const uploadableFiles = storedFiles?.conversationId === conversationId ? newFiles : uploadedFiles;

                const payload = {
                    conversation: lastConversation,
                    conversationId: conversationId,
                    files: uploadableFiles,
                    question: questionInput,
                };

                dispatch(DocumentSearchAction(payload));
                setQuestionInput("")
            }
        }
    };

    useEffect(() => {
        dispatch(DocumentSearchReset())
    }, [])

    useEffect(() => {
        if (chatLoading) {
            structureChat({ loading: chatLoading })
        }
    }, [chatLoading])

    useEffect(() => {
        if (chatResponse) {
            setShowModelSelect(false)
            setConversationId(chatResponse?.conversationId)
            setLastConversation(chatResponse?.conversation)
            chatResponse?.conversationId && handleLocalStorage(chatResponse?.conversationId)
            structureChat({ answer: chatResponse?.responseText })
        }
    }, [chatResponse])

    useEffect(() => {
        if (chatError) {
            structureChat({ error: chatError.message })
        }
    }, [chatError])

    return (
        <div className="doc-research-main">
            {showHistorySection && (
                <div className="history-section">
                    <ConversationHistory newConversation={handleNewConversation} selectedModel={selectedModel} showModels={SHOW_MODELS} />
                </div>
            )}
            <div className="expand-collapse-section-container">
                <div className="button-wrapper expand-collapse-button" onClick={() => handleExpandCollapse()}>
                    {showHistorySection ? <FaChevronRight /> : <FaChevronLeft />}
                </div>
                {(!showHistorySection && chatData?.length > 0) && (
                    <Tooltip
                        title="New Chat"
                        placement="right"
                    >
                        <div className="button-wrapper new-chat-button" onClick={() => handleNewConversation({ model: selectedModel })}>
                            <FontAwesomeIcon icon={faPlus} />
                        </div>
                    </Tooltip>
                )}
                {(selectedModel?.title) && (
                    <Tooltip
                        title={selectedModel?.title}
                        placement="right"
                    >
                        <div className="button-wrapper selected-model-button">
                            {selectedModel?.icon}
                        </div>
                    </Tooltip>
                )}
            </div>
            <div className="chat-section">
                <div className="chat-container" ref={scrollRef}>
                    <div className="conversation-chat-main">
                        {(SHOW_MODELS && showModelSelect && !selectedModel) && (
                            <InitializeChat selectModel={handleNewConversation} />
                        )}
                        {chatData.map((chat, index) => {
                            return (
                                <ConversationChat chat={chat} key={index} chatIndex={index} />
                            )
                        })}
                    </div>
                </div>
                <ChatInput
                    showDragger
                    chatLoading={chatLoading}
                    questionInput={questionInput}
                    uploadedFiles={uploadedFiles}
                    setQuestionInput={setQuestionInput}
                    setUploadedFiles={setUploadedFiles}
                    handleSubmit={handleSubmit}
                />
            </div>
        </div>
    )
}

export default DocResearch